import React, { useEffect, useMemo } from "react";
import { SettlementType } from "../../../../../requests_cm/gecoContractsService/types";
import { FluidNumber } from "../../../../../common/components/FluidNumberField";
import { Alert, Box, Typography } from "@mui/material";
import { FormikErrors } from "formik";
import FluidSelect from "../../../../../common/components/FluidSelect";
import * as _ from "lodash";
import { PayoutMethod } from "../../../../../common/constants/payout_methods";
import { If } from "../../../../../common/components/If";

export interface CapFloorInputsProps {
	setFieldValue: (
		field: string,
		value: any,
		shouldValidate?: boolean
	) => void;
	handleChange: {
		(e: React.ChangeEvent<any>): void;
		<T = string | React.ChangeEvent<any>>(
			field: T
		): T extends React.ChangeEvent<any>
			? void
			: (e: string | React.ChangeEvent<any>) => void;
	};
	values: SettlementType;
	namePrefix: string;
	errors: FormikErrors<SettlementType>;
	title: string;
}

const style = {
	container: {
		display: "flex",
		flexDirection: "column",
		gap: "12px",
	},
	inputs: {
		display: "flex",
		flexDirection: "row",
		justifyContent: "space-evenly",
		gap: "12px",
	},
};

export function CapFloorInputs(props: Readonly<CapFloorInputsProps>) {
	useEffect(() => {
		const payoutMethod = _.get(
			props.values,
			`${props.namePrefix}.payout_method`
		);
		if (payoutMethod === null || payoutMethod === undefined) {
			props.setFieldValue(
				`${props.namePrefix}.payout_method`,
				PayoutMethod.NONE
			);
		}
	}, [props.namePrefix, props.values, props.setFieldValue]);

	const payoutMethod = useMemo(
		() =>
			_.get(props.values, `${props.namePrefix}.payout_method`) ||
			PayoutMethod.NONE,
		[props.namePrefix, props.values]
	);

	const capIsDisabled = useMemo(
		() => [PayoutMethod.FLOOR, PayoutMethod.NONE].includes(payoutMethod),
		[payoutMethod]
	);

	const floorIsDisabled = useMemo(
		() => [PayoutMethod.CAP, PayoutMethod.NONE].includes(payoutMethod),
		[payoutMethod]
	);

	useEffect(() => {
		if (floorIsDisabled) {
			props.setFieldValue(`${props.namePrefix}.floor`, null);
		}
		if (capIsDisabled) {
			props.setFieldValue(`${props.namePrefix}.cap`, null);
		}
	}, [capIsDisabled, floorIsDisabled, props.values, props.setFieldValue]);

	const globalErrorMessage = useMemo(() => {
		const globalError = _.get(props.errors, props.namePrefix);
		if (_.isString(globalError)) {
			return globalError;
		}
		return null;
	}, [props.errors, props.namePrefix]);

	return (
		<Box sx={style.container}>
			<Typography variant={"overline"}>{props.title}</Typography>
			<If condition={globalErrorMessage}>
				<Alert severity="error">{globalErrorMessage}</Alert>
			</If>
			<Box sx={style.inputs}>
				<FluidSelect
					sx={{ width: "60%" }}
					isLabelStatic
					items={[
						{
							value: PayoutMethod.NONE,
							label: PayoutMethod.NONE,
						},
						{
							value: PayoutMethod.CAP,
							label: PayoutMethod.CAP,
						},
						{
							value: PayoutMethod.FLOOR,
							label: PayoutMethod.FLOOR,
						},
						{
							value: PayoutMethod.COLLAR,
							label: PayoutMethod.COLLAR,
						},
					]}
					label={"Payout method"}
					name={`${props.namePrefix}.payout_method`}
					onChange={(value) => {
						props.setFieldValue(
							`${props.namePrefix}.payout_method`,
							value
						);
					}}
					value={_.get(
						props.values,
						`${props.namePrefix}.payout_method`
					)}
					// @ts-ignore
					errorMessage={_.get(
						props.errors,
						`${props.namePrefix}].payout_method`
					)}
				/>
				<FluidNumber
					onChange={props.handleChange}
					value={_.get(props.values, `${props.namePrefix}.floor`)}
					name={`${props.namePrefix}.floor`}
					title="Floor"
					//@ts-ignore
					errorMessage={_.get(
						props.errors,
						`${props.namePrefix}.floor`
					)}
					isDisabled={floorIsDisabled}
				/>
				<FluidNumber
					onChange={props.handleChange}
					value={_.get(props.values, `${props.namePrefix}.cap`)}
					name={`${props.namePrefix}.cap`}
					title="Cap"
					//@ts-ignore
					errorMessage={_.get(
						props.errors,
						`${props.namePrefix}.cap`
					)}
					isDisabled={capIsDisabled}
				/>
			</Box>
		</Box>
	);
}
